// Pure decision functions extracted from ChartCanvas.startRAFLoop / handleZoom.
//
// They live here (instead of inline in the class methods) so they can be
// unit-tested without mounting ChartCanvas (vitest runs without jsdom). The
// class methods import and delegate to these — the behaviour is identical,
// only the gate/redraw/clamp arithmetic moved out.
//
// Block: render-core. Serves Directive 8 (visual feedback never stalls, 60fps)
// and the I5/I6 invariants.

// --- I5 / D8 ------------------------------------------------------------
// The RAF draw loop must SKIP (re-schedule without drawing) while any gesture
// lock is held, otherwise it would fight pan/zoom/drag/React updates. If any of
// these flags is stuck true, the loop stays alive but the chart freezes
// visually forever — exactly the #337 alt-tab symptom. This predicate is the
// single source of truth for that gate.
export function isRafBlocked(state) {
  return !!(
    state.panInProgress ||
    state.zoomInProgress ||
    state.yAxisDragInProgress ||
    state.reactUpdateInProgress
  );
}

// --- I6 + I5 / D8, D2, D7 ----------------------------------------------
// The RAF loop only redraws when it believes the data changed. Today it detects
// change by (a) plot length and (b) IDENTITY of the last bar. history-seam
// mutates the bar array IN-PLACE and only bumps dataVersion, so an OHLC update
// of the CURRENT bar keeps the same length AND the same last-bar reference →
// this returns false and the price update is NOT redrawn by the RAF loop
// (stale price disguised as live). This function pins that exact behaviour: it
// must return true on first draw (no prior length) and on length change, and —
// the documented weakness — false when only the last bar's contents mutate
// in-place with an unchanged reference.
export function shouldRafRedraw(
  lastDrawnDataLength,
  lastDrawnBar,
  currentDataLength,
  currentLastBar,
) {
  return (
    !lastDrawnDataLength ||
    lastDrawnDataLength !== currentDataLength ||
    lastDrawnBar !== currentLastBar
  );
}

// --- I5 / D8 ------------------------------------------------------------
// Anti-crash clamp for a fast wheel spin. Accumulated zoom-in steps can shrink
// the visible domain below ChartCanvas's ≤1-bar dev guard, which throws into
// the ErrorBoundary. Abort the zoom-in when the resulting plot would drop below
// MIN_PLOT_POINTS — but ONLY when fullData actually has enough bars to satisfy
// the threshold, so legitimately sparse charts still zoom.
export const MIN_PLOT_POINTS = 5;

export function shouldAbortZoomIn(totalZoomSteps, plotLen, fullLen) {
  return (
    totalZoomSteps < 0 &&
    plotLen < MIN_PLOT_POINTS &&
    fullLen >= MIN_PLOT_POINTS
  );
}
